import { ExternalLink, PlayCircle } from "lucide-react";

const DIRECT_VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm"];

export function getVideoPreview(url) {
  if (!url) return { type: "none", url: "" };
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const path = parsed.pathname;

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (path.startsWith("/embed/")) return { type: "iframe", url };
      if (path.startsWith("/shorts/")) {
        const id = path.split("/").filter(Boolean)[1];
        return id ? { type: "iframe", url: `https://www.youtube.com/embed/${id}` } : { type: "unsupported", url };
      }
      const id = parsed.searchParams.get("v");
      return id ? { type: "iframe", url: `https://www.youtube.com/embed/${id}` } : { type: "unsupported", url };
    }

    if (host === "youtu.be") {
      const id = path.split("/").filter(Boolean)[0];
      return id ? { type: "iframe", url: `https://www.youtube.com/embed/${id}` } : { type: "unsupported", url };
    }

    if (host === "vimeo.com" || host === "player.vimeo.com") {
      if (host === "player.vimeo.com" && path.startsWith("/video/")) return { type: "iframe", url };
      const id = path.split("/").filter(Boolean).find((part) => /^\d+$/.test(part));
      return id ? { type: "iframe", url: `https://player.vimeo.com/video/${id}` } : { type: "unsupported", url };
    }

    if (DIRECT_VIDEO_EXTENSIONS.some((ext) => path.toLowerCase().endsWith(ext))) {
      return { type: "video", url };
    }
  } catch (err) {
    return { type: "unsupported", url };
  }

  return { type: "unsupported", url };
}

export default function VideoPreview({ url, title = "Exercise video", compact = false }) {
  const preview = getVideoPreview(url);
  if (!url) {
    return (
      <div className="aspect-video rounded-2xl bg-slate-100 grid place-items-center text-slate-400" data-testid="video-placeholder">
        <div className="flex flex-col items-center gap-1">
          <PlayCircle size={32} />
          <span className="text-xs">Video coming soon</span>
        </div>
      </div>
    );
  }

  // Prefer YouTube, Vimeo, or direct video files for in-app playback; OneDrive/Google Drive links often block iframe embedding and should remain external links.
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 text-white">
      {preview.type === "iframe" && (
        <div className="aspect-video bg-black">
          <iframe
            title={title}
            src={preview.url}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      )}
      {preview.type === "video" && (
        <video controls className="aspect-video h-full w-full bg-black">
          <source src={preview.url} />
        </video>
      )}
      {preview.type === "unsupported" && (
        <div className={`${compact ? "p-4" : "aspect-video"} bg-slate-900 grid place-items-center text-center`}>
          <div className="px-4">
            <PlayCircle size={32} className="mx-auto text-slate-300" />
            <div className="mt-2 text-sm text-slate-100">Video preview unavailable for this link</div>
          </div>
        </div>
      )}
      <div className="px-4 py-3 flex items-center justify-between gap-3 bg-slate-900 text-slate-100">
        <span className="text-sm truncate">{preview.type === "unsupported" ? "Open video externally" : "Watch exercise video"}</span>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-300">
          Open video <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
}
